import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { CalendarClock, FileWarning, Receipt, Users, Zap, ZapOff } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  StayCountLabel,
  StayGuestCell,
  StayLines,
  StayListToolbar,
  StaySortHeader,
  StayStat,
} from "@/components/bookings/stayList";
import {
  formatPartyLine,
  matchesStayQuery,
  nextStaySort,
  nightsLabel,
  useFilteredStayRows,
  useStayListState,
} from "@/components/bookings/stayListModel";
import { ClickableTableRow } from "@/components/ui/ClickableTableRow";
import { Table } from "@/components/ui/Table";
import { countNightsLocal, formatIsoDateShort } from "@/lib/bookingDates";
import {
  draftInvoiceDisplayAmount,
  hasMissingInvoice,
  hasOpenInvoiceAmount,
  openInvoiceCount,
} from "@/lib/bookingInvoiceDisplay";
import { hasBookingDraftInvoice } from "@/lib/bookingInvoiceStatus";
import { buildBookingsOpenUrl } from "@/lib/bookingNavigation";
import { pitchCategoryName } from "@/lib/bookingPitchDisplay";
import { resolveGuestName } from "@/lib/guestDisplay";
import { formatCurrency } from "@/lib/money";
import { formatLicensePlate } from "@/lib/vehicle";
import type { BookingDeparture, Pitch, PitchCategory } from "@/types/camping";

type GuestFilter = "all" | "due" | "open" | "missing" | "power" | "draft";

const FILTER_EMPTY: Record<GuestFilter, string> = {
  all: "Keine eingecheckten Gäste am Platz.",
  due: "Keine fällige Abreise. Alle Gäste bleiben noch.",
  open: "Keine offenen Rechnungen.",
  missing: "Alle Aufenthalte haben eine Rechnung.",
  power: "Kein Gast mit gebuchtem Strom.",
  draft: "Keine Rechnungsentwürfe.",
};

function pitchCodeOf(booking: BookingDeparture, pitches: Pitch[]): string {
  if (!booking.pitch_id) return "Offen";
  return pitches.find((pitch) => pitch.id === booking.pitch_id)?.code ?? booking.pitch_id.slice(0, 8);
}

function pitchSortKey(code: string): string {
  return code === "Offen" ? "\uffff" : code;
}

function categoryOf(
  booking: BookingDeparture,
  pitches: Pitch[],
  categories: PitchCategory[],
): string | null {
  const pitch = booking.pitch_id ? pitches.find((item) => item.id === booking.pitch_id) : undefined;
  if (pitch?.category_name) return pitch.category_name;
  const categoryId = pitch?.category_id ?? booking.pitch_category_id;
  if (!categoryId) return null;
  const name = pitchCategoryName(categoryId, categories);
  return name === "—" || name === "Ohne Kategorie" ? null : name;
}

function departureState(booking: BookingDeparture, today: string): "overdue" | "today" | "later" {
  if (booking.departure_date < today) return "overdue";
  if (booking.departure_date === today) return "today";
  return "later";
}

function stayPrimary(booking: BookingDeparture, today: string): string {
  const state = departureState(booking, today);
  if (state === "overdue") {
    const days = countNightsLocal(booking.departure_date, today);
    return days <= 1 ? "Abreise überfällig" : `Abreise seit ${days} Tagen überfällig`;
  }
  if (state === "today") return "Abreise heute";
  const left = countNightsLocal(today, booking.departure_date);
  if (left <= 1) return "Abreise morgen";
  return `Noch ${left} Nächte`;
}

function formatKwh(value: string | null | undefined): string | null {
  if (value == null || value === "") return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return value;
  return `${num.toLocaleString("de-DE", { maximumFractionDigits: 1 })} kWh`;
}

function countPhrase(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

function matchesFilter(booking: BookingDeparture, filter: GuestFilter, today: string): boolean {
  switch (filter) {
    case "due":
      return booking.departure_date <= today;
    case "open":
      return hasOpenInvoiceAmount(booking);
    case "missing":
      return hasMissingInvoice(booking);
    case "power":
      return Boolean(booking.wants_electricity);
    case "draft":
      return hasBookingDraftInvoice(booking);
    default:
      return true;
  }
}

function balanceSortValue(booking: BookingDeparture): number {
  if (hasOpenInvoiceAmount(booking)) return Number(booking.open_invoice_amount) || 0;
  if (hasMissingInvoice(booking)) return 0;
  if (hasBookingDraftInvoice(booking)) return -0.5;
  return -1;
}

export interface OnSiteGuestsBoardProps {
  bookings: BookingDeparture[];
  pitches: Pitch[];
  categories: PitchCategory[];
  today: string;
  loading: boolean;
  onOpen: (booking: BookingDeparture) => void;
}

export function OnSiteGuestsBoard({
  bookings,
  pitches,
  categories,
  today,
  loading,
  onOpen,
}: OnSiteGuestsBoardProps) {
  const { search, setSearch, sort, setSort } = useStayListState(today);
  const [filter, setFilter] = useState<GuestFilter>("all");

  const pitchCode = useCallback(
    (booking: BookingDeparture) => pitchCodeOf(booking, pitches),
    [pitches],
  );
  const guestName = useCallback(
    (booking: BookingDeparture) => resolveGuestName(booking.guest_id, { booking }),
    [],
  );

  const stats = useMemo(() => {
    let people = 0;
    let dogs = 0;
    let openCount = 0;
    let openAmount = 0;
    let missingCount = 0;
    let draftCount = 0;
    let withPower = 0;
    let dueToday = 0;
    let overdue = 0;
    for (const booking of bookings) {
      people += (booking.adults ?? 0) + (booking.children ?? 0);
      dogs += booking.dogs ?? 0;
      if (hasOpenInvoiceAmount(booking)) {
        openCount += 1;
        openAmount += Number(booking.open_invoice_amount) || 0;
      }
      if (hasMissingInvoice(booking)) missingCount += 1;
      if (hasBookingDraftInvoice(booking)) draftCount += 1;
      if (booking.wants_electricity) withPower += 1;
      const state = departureState(booking, today);
      if (state === "today") dueToday += 1;
      if (state === "overdue") overdue += 1;
    }
    const currency = bookings.find((booking) => booking.currency)?.currency ?? "EUR";
    return {
      total: bookings.length,
      people,
      dogs,
      openCount,
      openAmount,
      missingCount,
      draftCount,
      withPower,
      withoutPower: bookings.length - withPower,
      dueToday,
      overdue,
      due: dueToday + overdue,
      currency,
    };
  }, [bookings, today]);

  const ordered = useMemo(() => {
    const rows = bookings.filter((booking) => matchesFilter(booking, filter, today));
    rows.sort((a, b) => {
      const byPitch = pitchSortKey(pitchCode(a)).localeCompare(pitchSortKey(pitchCode(b)), "de", {
        numeric: true,
        sensitivity: "base",
      });
      if (byPitch !== 0) return byPitch;
      return a.booking_number.localeCompare(b.booking_number, "de", { numeric: true });
    });
    return rows;
  }, [bookings, filter, pitchCode, today]);

  const matches = useCallback(
    (booking: BookingDeparture, query: string) =>
      matchesStayQuery(query, [
        guestName(booking),
        booking.booking_number,
        pitchCode(booking),
        categoryOf(booking, pitches, categories),
        booking.license_plate,
        booking.key_number,
        booking.transponder_id,
        formatIsoDateShort(booking.departure_date),
      ]),
    [categories, guestName, pitchCode, pitches],
  );

  const sortValue = useCallback(
    (booking: BookingDeparture, key: string) => {
      switch (key) {
        case "guest":
          return guestName(booking);
        case "pitch":
          return pitchSortKey(pitchCode(booking));
        case "stay":
          return booking.departure_date;
        case "balance":
          return balanceSortValue(booking);
        case "power":
          return booking.wants_electricity ? 1 : 0;
        default:
          return booking.booking_number;
      }
    },
    [guestName, pitchCode],
  );

  const visible = useFilteredStayRows(ordered, search, sort, matches, sortValue);
  const filtering =
    filter !== "all" || (search.trim().length > 0 && visible.length !== stats.total);
  const showSkeleton = loading && bookings.length === 0;

  function chooseFilter(next: GuestFilter) {
    setFilter((current) => (current === next ? "all" : next));
  }

  const partyHint = [
    stats.people > 0 ? countPhrase(stats.people, "Person", "Personen") : null,
    stats.dogs > 0 ? countPhrase(stats.dogs, "Hund", "Hunde") : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <FilterCard
          label="Am Platz"
          value={showSkeleton ? "—" : String(stats.total)}
          hint={showSkeleton ? "wird geladen" : partyHint || "eingecheckte Aufenthalte"}
          icon={Users}
          tone="neutral"
          active={filter === "all"}
          disabled={showSkeleton}
          onClick={() => setFilter("all")}
        />
        <FilterCard
          label="Abreise"
          value={showSkeleton ? "—" : String(stats.due)}
          hint={
            showSkeleton
              ? "wird geladen"
              : stats.overdue > 0 && stats.dueToday > 0
                ? `${stats.dueToday} heute · ${countPhrase(stats.overdue, "überfällig", "überfällig")}`
                : stats.overdue > 0
                  ? countPhrase(stats.overdue, "überfällig", "überfällig")
                  : stats.dueToday > 0
                    ? "heute geplant"
                    : "keine fällige Abreise"
          }
          icon={CalendarClock}
          tone={stats.overdue > 0 ? "danger" : stats.dueToday > 0 ? "warning" : "neutral"}
          active={filter === "due"}
          disabled={showSkeleton}
          onClick={() => chooseFilter("due")}
        />
        <FilterCard
          label="Offen"
          value={showSkeleton ? "—" : String(stats.openCount)}
          hint={
            showSkeleton
              ? "wird geladen"
              : stats.openCount > 0
                ? formatCurrency(stats.openAmount, stats.currency)
                : "keine offenen Posten"
          }
          icon={Receipt}
          tone={stats.openCount > 0 ? "warning" : "ok"}
          active={filter === "open"}
          disabled={showSkeleton}
          onClick={() => chooseFilter("open")}
        />
        <FilterCard
          label="Ohne Rechnung"
          value={showSkeleton ? "—" : String(stats.missingCount)}
          hint={
            showSkeleton
              ? "wird geladen"
              : stats.missingCount > 0
                ? "noch nicht angelegt"
                : "alle haben eine Rechnung"
          }
          icon={FileWarning}
          tone={stats.missingCount > 0 ? "warning" : "ok"}
          active={filter === "missing"}
          disabled={showSkeleton}
          onClick={() => chooseFilter("missing")}
        />
        <FilterCard
          label="Strom an"
          value={showSkeleton ? "—" : String(stats.withPower)}
          hint={
            showSkeleton
              ? "wird geladen"
              : stats.withoutPower > 0
                ? countPhrase(stats.withoutPower, "ohne Strom", "ohne Strom")
                : "alle mit Strom"
          }
          icon={Zap}
          tone="neutral"
          active={filter === "power"}
          disabled={showSkeleton}
          className="col-span-2 sm:col-span-1"
          onClick={() => chooseFilter("power")}
        />
      </div>

      {showSkeleton ? (
        <div className="h-72 animate-pulse rounded-2xl bg-fill-tertiary" aria-hidden />
      ) : (
        <Table
          className="eh-table--stay"
          isEmpty={visible.length === 0}
          emptyMessage={
            search.trim() && ordered.length > 0 ? "Keine Treffer für die Suche." : FILTER_EMPTY[filter]
          }
          toolbar={
            <StayListToolbar
              search={search}
              onSearchChange={setSearch}
              placeholder="Gast, Platz, Nr., Kennzeichen, Schlüssel…"
              searchLabel="Gäste am Platz durchsuchen"
              stats={
                <>
                  <StayCountLabel
                    shown={visible.length}
                    total={stats.total}
                    singular="Gast"
                    plural="Gäste"
                    filtering={filtering}
                  />
                  {stats.draftCount > 0 ? (
                    <button
                      type="button"
                      aria-pressed={filter === "draft"}
                      onClick={() => chooseFilter("draft")}
                    >
                      <StayStat tone={filter === "draft" ? "accent" : "warn"}>
                        <strong>{stats.draftCount}</strong>
                        {stats.draftCount === 1 ? "Entwurf" : "Entwürfe"}
                      </StayStat>
                    </button>
                  ) : null}
                  {filter !== "all" ? (
                    <button
                      type="button"
                      className="text-xs font-semibold text-accent hover:underline"
                      onClick={() => setFilter("all")}
                    >
                      Alle anzeigen
                    </button>
                  ) : null}
                </>
              }
            />
          }
        >
          <thead>
            <tr>
              <StaySortHeader
                label="Gast"
                sortKey="guest"
                sort={sort}
                onSort={(key) => setSort((current) => nextStaySort(current, key))}
              />
              <StaySortHeader
                label="Platz"
                sortKey="pitch"
                sort={sort}
                onSort={(key) => setSort((current) => nextStaySort(current, key))}
              />
              <StaySortHeader
                label="Aufenthalt"
                sortKey="stay"
                sort={sort}
                onSort={(key) => setSort((current) => nextStaySort(current, key))}
              />
              <StaySortHeader
                label="Rechnung"
                sortKey="balance"
                sort={sort}
                onSort={(key) => setSort((current) => nextStaySort(current, key))}
              />
              <StaySortHeader
                label="Strom"
                sortKey="power"
                sort={sort}
                onSort={(key) => setSort((current) => nextStaySort(current, key))}
              />
            </tr>
          </thead>
          <tbody>
            {visible.map((booking) => {
              const state = departureState(booking, today);
              const open = hasOpenInvoiceAmount(booking);
              const missing = hasMissingInvoice(booking);
              const draft = hasBookingDraftInvoice(booking);
              const paid = (booking.invoice_count ?? 0) > 0 && !open && !missing && !draft;
              const pitch = pitchCode(booking);
              const category = categoryOf(booking, pitches, categories);
              const plate = booking.license_plate
                ? formatLicensePlate(booking.license_plate, booking.license_plate_country)
                : null;
              const keyNumber = booking.key_number?.trim();
              const meta = [booking.booking_number, plate, keyNumber ? `Schlüssel ${keyNumber}` : null]
                .filter(Boolean)
                .join(" · ");
              const pitchRecord = booking.pitch_id
                ? pitches.find((item) => item.id === booking.pitch_id)
                : undefined;
              const kwh = formatKwh(booking.electricity_kwh);
              return (
                <ClickableTableRow
                  key={booking.id}
                  ariaLabel={`Buchung ${booking.booking_number} öffnen`}
                  onActivate={() => onOpen(booking)}
                  className={
                    state === "overdue"
                      ? "eh-stay-row--danger"
                      : open || missing
                        ? "eh-stay-row--warn"
                        : undefined
                  }
                >
                  <td data-label="Gast">
                    <StayGuestCell
                      name={guestName(booking)}
                      meta={meta}
                      badges={
                        state === "overdue" ? (
                          <span className="rounded-full bg-status-red/15 px-2 py-0.5 text-[0.65rem] font-semibold text-status-red">
                            Überfällig
                          </span>
                        ) : state === "today" ? (
                          <span className="rounded-full bg-accent-soft px-2 py-0.5 text-[0.65rem] font-semibold text-accent">
                            Heute
                          </span>
                        ) : null
                      }
                    />
                  </td>
                  <td data-label="Platz">
                    <div className="eh-stay-pitch">
                      <span className={clsx("eh-stay-pitch__code", pitch === "Offen" && "is-open")}>
                        {pitch}
                      </span>
                      {category ? (
                        <span className="eh-stay-pitch__meta">
                          <span className="eh-stay-pitch__cat">{category}</span>
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td data-label="Aufenthalt">
                    <StayLines
                      primary={stayPrimary(booking, today)}
                      secondary={`Anreise ${formatIsoDateShort(booking.arrival_date)} · Abreise ${formatIsoDateShort(booking.departure_date)} · ${nightsLabel(booking.nights)} · ${formatPartyLine(booking)}`}
                    />
                  </td>
                  <td data-label="Rechnung">
                    <InvoiceCell booking={booking} open={open} missing={missing} draft={draft} paid={paid} onOpen={onOpen} />
                  </td>
                  <td data-label="Strom">
                    {booking.wants_electricity ? (
                      <span className="eh-stay-power eh-stay-power--on">
                        <Zap aria-hidden />
                        <span>
                          An
                          {kwh ? <small>{kwh}</small> : null}
                        </span>
                      </span>
                    ) : (
                      <span className="eh-stay-power">
                        <ZapOff aria-hidden />
                        <span>
                          Aus
                          {pitchRecord && !pitchRecord.has_electricity ? <small>ohne Anschluss</small> : null}
                        </span>
                      </span>
                    )}
                  </td>
                </ClickableTableRow>
              );
            })}
          </tbody>
        </Table>
      )}
    </div>
  );
}

function InvoiceCell({
  booking,
  open,
  missing,
  draft,
  paid,
  onOpen,
}: {
  booking: BookingDeparture;
  open: boolean;
  missing: boolean;
  draft: boolean;
  paid: boolean;
  onOpen: (booking: BookingDeparture) => void;
}) {
  if (open) {
    return (
      <Link
        to={buildBookingsOpenUrl(booking.id, booking.status)}
        className="eh-stay-balance eh-stay-balance--open"
        onClick={(event) => {
          event.stopPropagation();
          onOpen(booking);
        }}
      >
        <span>{openInvoiceCount(booking) > 1 ? `${openInvoiceCount(booking)} offen` : "Offen"}</span>
        {formatCurrency(booking.open_invoice_amount, booking.currency)}
      </Link>
    );
  }
  if (missing) {
    return (
      <span className="eh-stay-balance eh-stay-balance--missing">
        <span>Rechnung</span>
        Fehlt
      </span>
    );
  }
  if (draft) {
    const amount = draftInvoiceDisplayAmount(booking);
    return (
      <span className="eh-stay-balance eh-stay-balance--draft">
        <span>Entwurf</span>
        {amount > 0 ? formatCurrency(amount, booking.currency) : "nicht ausgestellt"}
      </span>
    );
  }
  if (paid) {
    return (
      <span className="eh-stay-balance eh-stay-balance--paid">
        <span>Rechnung</span>
        Bezahlt
      </span>
    );
  }
  return <span className="text-xs text-label-tertiary">—</span>;
}

function FilterCard({
  label,
  value,
  hint,
  icon: Icon,
  tone,
  active,
  disabled,
  className,
  onClick,
}: {
  label: string;
  value: string;
  hint: string;
  icon: LucideIcon;
  tone: "neutral" | "ok" | "warning" | "danger";
  active: boolean;
  disabled?: boolean;
  className?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={clsx(
        className,
        "flex min-w-0 flex-col rounded-2xl border px-3.5 py-3 text-left shadow-ios transition",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-grouped",
        "disabled:cursor-default",
        tone === "danger" && "border-status-red/50 bg-status-red/10",
        tone === "warning" && "border-status-orange/45 bg-status-orange/10",
        tone === "ok" && "border-status-green/35 bg-status-green/10",
        tone === "neutral" && "border-separator bg-card hover:bg-fill-quaternary",
        active && tone === "danger" && "ring-2 ring-status-red",
        active && tone === "warning" && "ring-2 ring-status-orange",
        active && tone !== "danger" && tone !== "warning" && "ring-2 ring-accent",
        !disabled && tone === "neutral" && "hover:border-separator-opaque",
      )}
    >
      <span className="flex items-center justify-between gap-2">
        <span className="text-[0.6875rem] font-semibold uppercase tracking-wide text-label-secondary">
          {label}
        </span>
        <span
          className={clsx(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-xl",
            tone === "danger" && "bg-status-red/15 text-status-red",
            tone === "warning" && "bg-status-orange/20 text-status-orange",
            tone === "ok" && "bg-status-green/15 text-status-green",
            tone === "neutral" && "bg-fill-tertiary text-label-secondary",
          )}
        >
          <Icon size={16} aria-hidden />
        </span>
      </span>
      <span
        className={clsx(
          "mt-2 text-2xl font-semibold tabular-nums leading-none",
          tone === "danger" && "text-status-red",
          tone === "warning" && "text-status-orange",
          tone === "ok" && "text-status-green",
          tone === "neutral" && "text-label",
        )}
      >
        {value}
      </span>
      <span className="mt-1.5 text-xs leading-snug text-label-secondary">{hint}</span>
    </button>
  );
}
